/**
 * 3D product material configurator — Online Store theme (Liquid + <model-viewer>).
 *
 * model.materials mirrors glTF root "materials"[] (see @google/model-viewer Model: iterates gltf.materials).
 * Mesh / primitive count can be higher: multiple primitives may reference the same material index.
 *
 * Aligns with Hydrogen <ModelViewer /> behaviour: wait for scene-graph-ready
 * (same hook as onSceneGraphReady in @shopify/hydrogen) before mutating
 * model.materials via the official scene graph API — not raw Three.js traverse.
 * @see https://shopify.dev/docs/api/hydrogen/latest/components/media/modelviewer
 * @see https://modelviewer.dev/examples/scenegraph/
 *
 * Product metafields mirror Storefront API JSON in metafield.value (custom.fabrics, custom.leg / custom.legs).
 * Fabric swatch JPGs are tiled on baseColorTexture (model-viewer sampler repeat), not stretched once across UVs.
 * Fabric tile repeat: per color (or fabric) in custom.fabrics via texture_repeat_u/v or texture_repeat: { u, v } (default 4×4).
 * Targets glTF *material* names (Outliner → Material datablock), not mesh object names.
 * After splitting meshes, Blender often renames materials to Material / Material.001 — use
 * materialIndex (glTF materials[] order) from gltf-viewer or add ?s3d_debug=1 to log indices.
 * Single-material GLBs: optional fallback to materials[0] when names do not match (data-s3d-single-material-fallback, theme block setting).
 * GLB prep aligns with Shopify’s 3D partner docs (checklist + Blender/Substance): PBR Metal/Rough GLB, real-world scale; see window.__S3D_GLB_PREP__ in devtools.
 * @see https://modelviewer.dev/examples/scenegraph/
 */
(function () {
  'use strict';

  /** Merchant/dev reference: type __S3D_GLB_PREP__ in the browser console on a product page. */
  var S3D_GLB_PREP = {
    links: {
      shopifyChecklist:
        'https://help.shopify.com/en/partners/resources/creating-media/3d-models/creating-3d-models/3d-modeling-checklist',
      blenderAndSubstance:
        'https://help.shopify.com/en/partners/resources/creating-media/3d-models/creating-3d-models/blender',
      modelViewerSceneGraph: 'https://modelviewer.dev/examples/scenegraph/',
      gltfReport: 'https://gltf.report/',
    },
    howThisThemeWorks:
      'Shopify hosts your GLB; this script uses <model-viewer>\'s scene graph to call pbrMetallicRoughness.setBaseColorFactor (linear RGBA) and tiled baseColorTexture (sampler repeat, default 4×4). It targets glTF materials[] by name or materialIndex — not mesh object names.',
    steps: [
      'Follow Shopify’s 3D modeling checklist: accurate scale, topology, and file limits for AR / web.',
      'Use PBR Metallic-Roughness materials compatible with glTF 2.0 (common path: Blender glTF exporter or Substance Painter glTF PBR export).',
      'For independent fabric vs legs (etc.), assign a different Blender material to each part so the GLB has multiple materials[] entries — not just multiple meshes sharing one material.',
      'Name each material in Blender before export; names flow into glTF (verify in gltf.report). Unnamed materials are hard to target from metafields.',
      'Upload the GLB as the product 3D model. In metafields, set custom.fabrics / custom.leg JSON with "material" / "materials" and/or "materialIndex" matching gltf.report order (0, 1, 2…).',
      'If the GLB has only one material, enable "Apply when GLB has one material" in the theme block so swatches tint that slot, or re-export with split materials for separate control.',
      'Debug material names: add ?s3d_debug=1 to the product URL (or localStorage s3d_debug=1), reload, open the 3D viewer once.',
    ],
  };

  if (typeof window !== 'undefined') {
    try {
      window.__S3D_GLB_PREP__ = S3D_GLB_PREP;
    } catch (ignore) {}
  }

  var S3D_DEBUG =
    typeof window !== 'undefined' &&
    window.location &&
    /[?&]s3d_debug=1(?:&|$)/.test(String(window.location.search || ''));

  /** URL ?s3d_debug=1, localStorage s3d_debug=1, or window.__S3D_DEBUG__ = true */
  function s3dDebugEnabled() {
    if (typeof window === 'undefined') return false;
    try {
      if (window.__S3D_DEBUG__ === true) return true;
      if (typeof localStorage !== 'undefined' && localStorage.getItem('s3d_debug') === '1') return true;
    } catch (ignore) {}
    return S3D_DEBUG;
  }

  function s3dTrace(step, detail) {
    if (!s3dDebugEnabled()) return;
    if (detail !== undefined) {
      console.info('[s3d-material-configurator]', step, detail);
    } else {
      console.info('[s3d-material-configurator]', step);
    }
  }

  var warnedNoModelRoots = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
  var warnedNoBaseColorTextureSlot = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
  /** Log once per model when we refuse fallback or indices are out of range. */
  var singleMaterialFallbackLogged =
    typeof WeakMap !== 'undefined' ? new WeakMap() : null;
  /** Log once per model when we apply fabric/legs to the sole material (shared look). */
  var singleMaterialSharedInfoLogged =
    typeof WeakMap !== 'undefined' ? new WeakMap() : null;
  /** Dedupe "No materials matched" spam (same model + same config signature). */
  var s3dNoMaterialMatchLogged = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
  /** load / scene-graph-ready may not bubble; bind once per <model-viewer> element. */
  var viewerGraphDirectBound = new WeakSet();

  function s3dShouldEmitModelWarning(model, key) {
    if (!s3dNoMaterialMatchLogged || !model || typeof model !== 'object') return true;
    var set = s3dNoMaterialMatchLogged.get(model);
    if (!set) {
      set = new Set();
      s3dNoMaterialMatchLogged.set(model, set);
    }
    if (set.has(key)) return false;
    set.add(key);
    return true;
  }

  function logModelViewerSource(mv, label) {
    if (!s3dDebugEnabled() || !mv) return;
    var src = mv.getAttribute && mv.getAttribute('src');
    var ios = mv.getAttribute && mv.getAttribute('ios-src');
    var n = mv.model && mv.model.materials ? mv.model.materials.length : 0;
    var names =
      mv.model && mv.model.materials
        ? mv.model.materials.map(function (m, i) {
            return i + ':' + JSON.stringify(m && m.name != null ? m.name : '');
          })
        : [];
    console.info(
      '[s3d-material-configurator] DEBUG',
      label || '',
      '\n  src:',
      src,
      '\n  ios-src:',
      ios,
      '\n  model.materials.length:',
      n,
      '\n  names:',
      names.join(', ') || '(none)'
    );
  }

  function parseJsonScript(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try {
      var t = el.textContent.trim();
      if (!t || t === 'null') return null;
      return JSON.parse(t);
    } catch (e) {
      console.warn('[s3d-material-configurator] Invalid JSON in', id, e);
      return null;
    }
  }

  /** Metafield JSON may be object or double-encoded string */
  function coerceConfigObject(parsed) {
    if (parsed == null) return {};
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch (e) {
        return {};
      }
    }
    if (typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed;
  }

  /** Flatten metafield values (Shopify may nest { value: "..." } in some APIs). */
  function flattenTargetToken(n) {
    if (n == null) return '';
    if (typeof n === 'string' || typeof n === 'number') return String(n).trim();
    if (typeof n === 'object' && !Array.isArray(n)) {
      if (typeof n.value === 'string' || typeof n.value === 'number') return String(n.value).trim();
    }
    return String(n).trim();
  }

  function normalizeTargets(names) {
    if (!names) return [];
    var arr = Array.isArray(names) ? names : [names];
    return arr.map(flattenTargetToken).filter(Boolean);
  }

  /**
   * Blender / glTF exporters sometimes use Material.headboard vs Material_Headboard vs "Material headboard".
   */
  function expandMaterialTargetTokens(names) {
    var base = normalizeTargets(names);
    var out = [];
    var seen = new Set();
    var i;
    var t;
    var u;
    function push(s) {
      if (!s || seen.has(s)) return;
      seen.add(s);
      out.push(s);
    }
    for (i = 0; i < base.length; i++) {
      t = base[i];
      push(t);
      u = t.replace(/\./g, '_');
      if (u !== t) push(u);
      u = t.replace(/\./g, ' ');
      if (u !== t) push(u);
    }
    return out;
  }

  /**
   * Leg / fabric entries may use materials (array), material (string), or materials: [] from Shopify.
   */
  function resolveMaterialTargetField(entry) {
    if (!entry || typeof entry !== 'object') return null;
    var m = entry.materials;
    if (m != null) {
      if (Array.isArray(m) && m.length > 0) return m;
      if (!Array.isArray(m) && flattenTargetToken(m)) return m;
    }
    if (entry.material != null && flattenTargetToken(entry.material)) return entry.material;
    return null;
  }

  /** Last segment after final dot, lowercased (Material.sheet → sheet). */
  function materialNameBasename(name) {
    var n = String(name || '').trim().toLowerCase();
    var d = n.lastIndexOf('.');
    return d >= 0 ? n.slice(d + 1) : n;
  }

  /**
   * True when a glTF material name matches a metafield target.
   * Handles exact match, case-insensitive match, basename match, and Blender duplicate
   * suffixes — e.g. target "Grey_Mat_1001" matches "Grey_Mat_1001.007", ".009", etc.
   */
  function materialMatchesTarget(materialName, targetName) {
    var n = String(materialName || '').trim();
    var t = String(targetName || '').trim();
    if (!n || !t) return false;
    if (n === t) return true;
    var nl = n.toLowerCase();
    var tl = t.toLowerCase();
    if (nl === tl) return true;
    var nb = materialNameBasename(n);
    if (materialNameBasename(t) === nb || tl === nb) return true;
    if (nl.indexOf(tl + '.') === 0) return true;
    return false;
  }

  /**
   * Resolve model-viewer Material objects by glTF material name.
   * Collects every slot that matches (exact, basename, or Blender .00N suffix).
   */
  function materialsForTargets(model, targetNames) {
    var materials = model.materials || [];
    var targets = expandMaterialTargetTokens(targetNames);
    if (!targets.length) return [];

    var out = [];
    var seen = new Set();
    var i, j, mat, n;

    for (i = 0; i < materials.length; i++) {
      mat = materials[i];
      n = String(mat.name || '').trim();
      if (!n) continue;
      for (j = 0; j < targets.length; j++) {
        if (materialMatchesTarget(n, targets[j])) {
          if (!seen.has(mat)) {
            seen.add(mat);
            out.push(mat);
          }
          break;
        }
      }
    }

    return out;
  }

  /**
   * Common leg/wood material names used across GLBs when metafields don't specify an
   * explicit material/materialIndex for legs. Tried by name (case-insensitive, basename,
   * and Blender .00N duplicate suffix — e.g. "Wood_1001.007") before falling back to a
   * blind index guess in applyLegs().
   */
  var LEG_MATERIAL_NAME_FALLBACKS = ['Wood_1001', 'Wood', 'Legs', 'Leg', 'Leg_1001', 'Legs_1001', 'Wood_Legs', 'Leg_Wood'];

  /** Blender base before trailing .00N duplicate suffix (Grey_Mat_1001.009 → Grey_Mat_1001). */
  function materialNameBlenderBase(name) {
    var n = String(name || '').trim();
    if (!n) return '';
    var m = n.match(/^(.+)\.\d+$/);
    return m ? m[1] : n;
  }

  /**
   * After resolving by index or a single name hit, expand to all Blender-numbered copies
   * of the same material (e.g. Grey_Mat_1001.007, .008, .009).
   */
  function expandToBlenderNumberedVariants(model, mats) {
    if (!model || !mats || !mats.length) return mats || [];
    var bases = {};
    var i;
    var base;
    for (i = 0; i < mats.length; i++) {
      base = materialNameBlenderBase(mats[i] && mats[i].name);
      if (base) bases[base] = true;
    }
    var targetNames = Object.keys(bases);
    if (!targetNames.length) return mats;
    var expanded = materialsForTargets(model, targetNames);
    return expanded.length ? expanded : mats;
  }

  /** Optional JSON: materialIndex / material_index / materialIndices / material_indices */
  function resolveMaterialIndices(entry) {
    if (!entry || typeof entry !== 'object') return null;
    var v =
      entry.materialIndex != null
        ? entry.materialIndex
        : entry.material_index != null
          ? entry.material_index
          : entry.materialIndices != null
            ? entry.materialIndices
            : entry.material_indices;
    if (v == null) return null;
    return Array.isArray(v) ? v : [v];
  }

  function materialsByGltfIndex(model, indices) {
    var list = model && model.materials ? model.materials : [];
    var out = [];
    var seen = new Set();
    var i;
    var n;
    var mat;
    for (i = 0; i < indices.length; i++) {
      n = Number(indices[i]);
      if (isNaN(n) || n < 0 || n >= list.length) continue;
      mat = list[n];
      if (mat && !seen.has(mat)) {
        seen.add(mat);
        out.push(mat);
      }
    }
    return out;
  }

  /** True when entry lists materialIndex values and every one is out of range (e.g. [1] with one material). */
  function materialIndicesAllInvalid(entry, listLength) {
    var indices = resolveMaterialIndices(entry);
    if (!indices || !indices.length) return false;
    var i;
    var n;
    for (i = 0; i < indices.length; i++) {
      n = Number(indices[i]);
      if (!isNaN(n) && n >= 0 && n < listLength) return false;
    }
    return true;
  }

  /**
   * Resolve materials for a fabric/leg JSON entry: getMaterialByName (with name variants), scan, then glTF index.
   * @param {{ allowSingleMaterialFallback?: boolean }} [opts] allowSingleMaterialFallback default true — use sole material when names are empty/unmatched unless every index is out of range.
   */
  function materialsForEntry(model, entry, opts) {
    opts = opts || {};
    var allowFallback = opts.allowSingleMaterialFallback !== false;
    var mats = [];
    var seen = new Set();
    var list = model && model.materials ? model.materials : [];
    function add(m) {
      if (m && !seen.has(m)) {
        seen.add(m);
        mats.push(m);
      }
    }
    var nameField = resolveMaterialTargetField(entry);
    if (nameField != null && !(Array.isArray(nameField) && nameField.length === 0)) {
      var expanded = expandMaterialTargetTokens(nameField);
      if (typeof model.getMaterialByName === 'function') {
        expanded.forEach(function (t) {
          var m = model.getMaterialByName(t);
          if (m) add(m);
        });
      }
      materialsForTargets(model, expanded).forEach(add);
    }
    var indices = resolveMaterialIndices(entry);
    if (indices && indices.length) {
      materialsByGltfIndex(model, indices).forEach(add);
    }
    /**
     * One material in file: names in metafields (e.g. Material.legs) often do not match viewer (unnamed slot).
     * Optional fallback to materials[0] so swatches still update the model. Skip when every materialIndex is
     * out of range — merchant expected a second material. Turn off with data-s3d-single-material-fallback="false".
     */
    if (!mats.length && list.length === 1 && list[0] && hasAnyMaterialSelector(entry)) {
      if (allowFallback && !materialIndicesAllInvalid(entry, list.length)) {
        add(list[0]);
        if (singleMaterialSharedInfoLogged && model && !singleMaterialSharedInfoLogged.has(model)) {
          singleMaterialSharedInfoLogged.set(model, true);
          console.info(
            '[s3d-material-configurator] Single material in GLB: fabric and leg options both update this slot. Split materials in Blender and verify materials[] at ' +
              S3D_GLB_PREP.links.gltfReport +
              ' — or type __S3D_GLB_PREP__ in the console for a full checklist.'
          );
        }
      } else if (!singleMaterialFallbackLogged || !singleMaterialFallbackLogged.has(model)) {
        if (singleMaterialFallbackLogged) singleMaterialFallbackLogged.set(model, true);
        if (materialIndicesAllInvalid(entry, list.length)) {
          console.warn(
            '[s3d-material-configurator] materialIndex is out of range — this model only has one material. Fix indices in metafields or re-export the GLB.',
            { targets: resolveMaterialTargetField(entry), indices: resolveMaterialIndices(entry) }
          );
        } else if (!allowFallback) {
          console.warn(
            '[s3d-material-configurator] Single-material fallback is off. Enable "Apply when GLB has one material" on the 3D materials block, or set materialIndex: 0 and matching names.'
          );
        }
      }
    }
    if (mats.length) {
      mats = expandToBlenderNumberedVariants(model, mats);
    }
    return mats;
  }

  function hasAnyMaterialSelector(entry) {
    var n = resolveMaterialTargetField(entry);
    var idx = resolveMaterialIndices(entry);
    var hasNames = n != null && !(Array.isArray(n) && n.length === 0);
    var hasIdx = idx && idx.length > 0;
    return hasNames || hasIdx;
  }

  /** True when URL is an absolute http(s) asset (skips placeholders like VELVET_BLACK_TEXTURE_URL). */
  function isHttpTextureUrl(url) {
    if (url == null || typeof url !== 'string') return false;
    return /^https?:\/\//i.test(url.trim());
  }

  /**
   * Resolve fabric color image URL from custom.fabrics JSON (3D texture + UI swatch).
   * Expected: colors[ColorName].texture_url (http/https). Legacy swatch field is fallback only.
   * Invalid/missing → '' (grey placeholder swatch; skip 3D apply).
   */
  function resolveFabricTextureUrl(entry, colorKey) {
    if (!entry || typeof entry !== 'object' || !colorKey) return '';
    if (!entry.colors || entry.colors[colorKey] === undefined) return '';

    var colorVal = entry.colors[colorKey];
    var url = '';

    if (colorVal && typeof colorVal === 'object') {
      url =
        colorVal.texture_url ||
        colorVal.textureUrl ||
        colorVal.swatch ||
        colorVal.swatchUrl ||
        '';
    }

    if (!isHttpTextureUrl(url) && entry.color_textures && typeof entry.color_textures === 'object') {
      url = entry.color_textures[colorKey] || '';
    }

    return isHttpTextureUrl(url) ? String(url).trim() : '';
  }

  /** glTF Wrap.Repeat / THREE.RepeatWrapping — model-viewer scene graph sampler API. */
  var S3D_WRAP_REPEAT = 10497;
  var S3D_DEFAULT_FABRIC_TEXTURE_REPEAT_U = 4;
  var S3D_DEFAULT_FABRIC_TEXTURE_REPEAT_V = 4;
  var S3D_MAX_FABRIC_TEXTURE_REPEAT = 64;

  function parseRepeatScalar(value, fallback) {
    if (value == null || value === '') return fallback;
    var n = typeof value === 'number' ? value : parseFloat(String(value).trim());
    if (!isFinite(n) || n <= 0) return fallback;
    return Math.min(n, S3D_MAX_FABRIC_TEXTURE_REPEAT);
  }

  /** Read texture_repeat / texture_repeat_u|v (and camelCase aliases) from a metafield object. */
  function readRepeatFromObject(obj) {
    if (!obj || typeof obj !== 'object') return null;
    var block = obj.texture_repeat || obj.textureRepeat;
    if (block && typeof block === 'object') {
      var u = block.u != null ? block.u : block.x;
      var v = block.v != null ? block.v : block.y;
      if (u != null || v != null) return { u: u, v: v };
    }
    var ru = obj.texture_repeat_u != null ? obj.texture_repeat_u : obj.textureRepeatU;
    if (ru == null) ru = obj.repeat_u != null ? obj.repeat_u : obj.repeatU;
    var rv = obj.texture_repeat_v != null ? obj.texture_repeat_v : obj.textureRepeatV;
    if (rv == null) rv = obj.repeat_v != null ? obj.repeat_v : obj.repeatV;
    if (ru != null || rv != null) return { u: ru, v: rv };
    return null;
  }

  /**
   * Tile count for fabric JPG on Grey_Mat_1001: color entry → fabric entry → 4×4 default.
   * @returns {{ u: number, v: number }}
   */
  function resolveFabricTextureRepeat(entry, colorKey) {
    var defU = S3D_DEFAULT_FABRIC_TEXTURE_REPEAT_U;
    var defV = S3D_DEFAULT_FABRIC_TEXTURE_REPEAT_V;
    var u = defU;
    var v = defV;

    var fromEntry = readRepeatFromObject(entry);
    if (fromEntry) {
      if (fromEntry.u != null) u = fromEntry.u;
      if (fromEntry.v != null) v = fromEntry.v;
    }

    if (colorKey && entry && entry.colors && entry.colors[colorKey]) {
      var colorVal = entry.colors[colorKey];
      if (colorVal && typeof colorVal === 'object') {
        var fromColor = readRepeatFromObject(colorVal);
        if (fromColor) {
          if (fromColor.u != null) u = fromColor.u;
          if (fromColor.v != null) v = fromColor.v;
        }
      }
    }

    return {
      u: parseRepeatScalar(u, defU),
      v: parseRepeatScalar(v, defV),
    };
  }

  /**
   * Tile fabric albedo via model-viewer sampler (Three.js texture.repeat + RepeatWrapping).
   * Call after baseColorTexture.setTexture — setTexture can copy the slot's prior transform.
   */
  function configureFabricTextureTiling(texture, repeatU, repeatV) {
    if (!texture || !texture.sampler) return false;
    var u = parseRepeatScalar(repeatU, S3D_DEFAULT_FABRIC_TEXTURE_REPEAT_U);
    var v = parseRepeatScalar(repeatV, S3D_DEFAULT_FABRIC_TEXTURE_REPEAT_V);
    try {
      if (typeof texture.sampler.setWrapS === 'function') {
        texture.sampler.setWrapS(S3D_WRAP_REPEAT);
      }
      if (typeof texture.sampler.setWrapT === 'function') {
        texture.sampler.setWrapT(S3D_WRAP_REPEAT);
      }
      if (typeof texture.sampler.setScale === 'function') {
        texture.sampler.setScale({ u: u, v: v });
      }
      return true;
    } catch (e) {
      if (s3dDebugEnabled()) {
        console.warn('[s3d-material-configurator] configureFabricTextureTiling', e);
      }
      return false;
    }
  }

  /** Apply tiling on each material's bound baseColorTexture (after setTexture). */
  function configureMaterialsFabricTiling(materials, repeatU, repeatV) {
    var configured = 0;
    var i;
    for (i = 0; i < materials.length; i++) {
      try {
        var pbr = materials[i].pbrMetallicRoughness;
        var info = pbr && pbr.baseColorTexture;
        var bound = info && info.texture;
        if (bound && configureFabricTextureTiling(bound, repeatU, repeatV)) configured++;
      } catch (ignore) {}
    }
    return configured;
  }

  function syncFabricColorStateFromUi(wrap, state) {
    if (!wrap || !state) return false;
    var activeFabric = wrap.querySelector('.s3d-mat-config__btn[data-s3d-fabric].is-active');
    if (!activeFabric) return false;
    var fk = activeFabric.getAttribute('data-s3d-fabric');
    if (!fk) return false;
    state.fabricKey = fk;
    var colorSection = wrap.querySelector('[data-s3d-color-section="' + fk + '"]');
    var activeColor =
      (colorSection && colorSection.querySelector('[data-s3d-color].is-active')) ||
      wrap.querySelector('[data-s3d-color].is-active');
    if (activeColor) {
      state.colorKey = activeColor.getAttribute('data-s3d-color');
    }
    return !!(state.fabricKey && state.colorKey);
  }

  /**
   * Write fabric + color selections into line-item property hidden inputs on the product form.
   * Dispatches s3d:cart-properties-sync so the 3d flow controller marks steps as interacted.
   */
  function syncCartLineItemProperties(wrap, fabricsData, state) {
    if (!wrap || !state) return;
    var sectionId = wrap.getAttribute('data-section-id');
    if (!sectionId) return;

    var fk = state.fabricKey;
    var ck = state.colorKey;
    var fabricProp = document.getElementById('fabric-prop-' + sectionId);
    var colorProp = document.getElementById('color-prop-' + sectionId);
    if (!fabricProp && !colorProp) return;

    var entry = fk && fabricsData ? fabricsData[fk] : null;
    var fabricLabel = (entry && entry.label) || fk || '';
    var colorLabel =
      ck ||
      (function () {
        var active = wrap.querySelector('[data-s3d-color].is-active');
        return active
          ? active.getAttribute('aria-label') ||
              active.getAttribute('title') ||
              active.getAttribute('data-s3d-color') ||
              ''
          : '';
      })();

    if (fabricProp && fabricLabel) {
      fabricProp.value = fabricLabel;
      fabricProp.removeAttribute('disabled');
    }
    if (colorProp && colorLabel) {
      colorProp.value = colorLabel;
      colorProp.removeAttribute('disabled');
    }

    wrap.dispatchEvent(
      new CustomEvent('s3d:cart-properties-sync', {
        bubbles: true,
        detail: {
          fabricKey: fk || null,
          colorKey: ck || colorLabel || null,
          fabricLabel: fabricLabel || null,
          colorLabel: colorLabel || null,
        },
      })
    );
  }

  function scheduleCartPropertySync(wrap, fabricsData, state) {
    setTimeout(function () {
      syncFabricColorStateFromUi(wrap, state);
      syncCartLineItemProperties(wrap, fabricsData, state);
    }, 30);
  }

  /** Neutral white base color factor so JPG albedo is not multiplied by a dark baked factor (not a fabric tint). */
  function resetFabricMaterialBaseFactor(materials) {
    applyBaseColor(materials, '#ffffff');
  }

  /** sRGB hex → linear RGBA for setBaseColorFactor array form (per glTF / model-viewer docs). */
  function hexToLinearRgba(hex) {
    var h = flattenTargetToken(hex).replace(/^#/, '');
    if (h.length === 3) {
      h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    }
    if (h.length !== 6) return null;
    var r = parseInt(h.slice(0, 2), 16);
    var g = parseInt(h.slice(2, 4), 16);
    var b = parseInt(h.slice(4, 6), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
    function lin(c) {
      c /= 255;
      return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    }
    return [lin(r), lin(g), lin(b), 1];
  }

  async function ensureMaterialsReady(materials) {
    for (var i = 0; i < materials.length; i++) {
      var m = materials[i];
      if (!m || typeof m.ensureLoaded !== 'function') continue;
      try {
        await m.ensureLoaded();
      } catch (e) {
        console.warn('[s3d-material-configurator] material.ensureLoaded', m && m.name, e);
      }
    }
  }

  /** Call before matching by material name — names often populate only after ensureLoaded. */
  async function ensureAllModelMaterialsLoaded(model) {
    if (!model || !model.materials || !model.materials.length) return;
    var list = model.materials;
    var i;
    var m;
    for (i = 0; i < list.length; i++) {
      m = list[i];
      if (m && typeof m.ensureLoaded === 'function') {
        try {
          await m.ensureLoaded();
        } catch (ignore) {}
      }
    }
  }

  /** model.materials can grow after the first scene-graph-ready; poll briefly. */
  async function waitForModelMaterialsStabilize(model, maxMs) {
    if (!model || !model.materials) return;
    var deadline = Date.now() + (maxMs != null ? maxMs : 1400);
    var lastCount = -1;
    var stablePasses = 0;
    while (Date.now() < deadline) {
      await ensureAllModelMaterialsLoaded(model);
      var n = model.materials ? model.materials.length : 0;
      if (n === lastCount) {
        stablePasses++;
        if (stablePasses >= 2) break;
      } else {
        stablePasses = 0;
        lastCount = n;
      }
      await new Promise(function (r) {
        setTimeout(r, 100);
      });
    }
  }

  /**
   * Rarely, material count can climb after the first stabilize pass (extensions / late graph build).
   * Only polls when we still see exactly one material.
   */
  async function waitForMaterialCountIncrease(model, stuckAt, maxMs) {
    if (!model || !model.materials) return;
    if (model.materials.length !== stuckAt || stuckAt !== 1) return;
    var deadline = Date.now() + (maxMs != null ? maxMs : 4000);
    while (Date.now() < deadline) {
      await new Promise(function (r) {
        setTimeout(r, 200);
      });
      await ensureAllModelMaterialsLoaded(model);
      var n = model.materials ? model.materials.length : 0;
      if (n > stuckAt) {
        s3dTrace('waitForMaterialCountIncrease: count increased', { was: stuckAt, now: n });
        return;
      }
    }
  }

  function absoluteUrlFromModelSrc(src) {
    if (!src || typeof src !== 'string') return '';
    var s = src.trim();
    if (s.indexOf('//') === 0) {
      return (typeof window !== 'undefined' && window.location && window.location.protocol ? window.location.protocol : 'https:') + s;
    }
    if (/^https?:\/\//i.test(s)) return s;
    try {
      if (typeof window !== 'undefined' && window.location) return new URL(s, window.location.origin).href;
    } catch (ignore) {}
    return '';
  }

  /**
   * Read glTF JSON chunk from GLB v2 (same source gltf.report uses) — no Three.js.
   * @returns {{ count: number, names: string[], extensionsUsed: string[] } | null}
   */
  function parseGlbJsonMaterialsMeta(buf) {
    if (!buf || buf.byteLength < 28) return null;
    var view = new DataView(buf);
    if (view.getUint32(0, true) !== 0x46546c67) return null;
    if (view.getUint32(4, true) !== 2) return null;
    var offset = 12;
    var guard = 0;
    while (offset + 8 <= buf.byteLength && guard < 24) {
      guard++;
      var chunkLength = view.getUint32(offset, true);
      var chunkType = view.getUint32(offset + 4, true);
      offset += 8;
      if (chunkType === 0x4e4f534a) {
        var jsonBytes = new Uint8Array(buf, offset, chunkLength);
        var text = new TextDecoder('utf-8').decode(jsonBytes);
        var gltf;
        try {
          gltf = JSON.parse(text);
        } catch (e) {
          return null;
        }
        var mats = gltf.materials;
        var count = Array.isArray(mats) ? mats.length : 0;
        var names = [];
        var i;
        for (i = 0; i < count; i++) {
          names.push(mats[i] && mats[i].name != null ? String(mats[i].name) : '(unnamed)');
        }
        return {
          count: count,
          names: names,
          extensionsUsed: Array.isArray(gltf.extensionsUsed) ? gltf.extensionsUsed : [],
        };
      }
      offset += chunkLength;
    }
    return null;
  }

  function compareRemoteGlbMaterialsToViewer(mv, src, viewerIndex) {
    var abs = absoluteUrlFromModelSrc(src);
    if (!abs) return Promise.resolve();
    /* JSON chunk is first in most GLBs; avoid downloading huge BIN (e.g. 40MB+ meshes). */
    var rangeEnd = 8 * 1024 * 1024 - 1;
    return fetch(abs, {
      mode: 'cors',
      credentials: 'omit',
      headers: { Range: 'bytes=0-' + rangeEnd },
    })
      .then(function (res) {
        if (res.status === 416) {
          return fetch(abs, { mode: 'cors', credentials: 'omit' }).then(function (r2) {
            if (!r2.ok) throw new Error('HTTP ' + r2.status);
            return r2.arrayBuffer();
          });
        }
        if (!res.ok && res.status !== 206) throw new Error('HTTP ' + res.status);
        return res.arrayBuffer();
      })
      .then(function (buf) {
        var parsed = parseGlbJsonMaterialsMeta(buf);
        var viewerN = materialCountOnViewer(mv);
        console.group(
          '[s3d-material-configurator] Live GLB (network) vs <model-viewer> (viewer ' + viewerIndex + ')'
        );
        console.info('Fetched:', abs);
        if (parsed) {
          console.info('GLB JSON materials.length:', parsed.count, '| names:', parsed.names.join(' | '));
          if (parsed.extensionsUsed.length) {
            console.info('extensionsUsed:', parsed.extensionsUsed.join(', '));
          }
        } else {
          console.warn(
            'Could not read GLB v2 JSON chunk (wrong format, parse error, or JSON chunk beyond first 8MB of file). Compare using the same URL in gltf.report.'
          );
        }
        console.info('model.materials.length:', viewerN);
        if (parsed && parsed.count !== viewerN) {
          console.warn(
            'MISMATCH: The file at src declares ' +
              parsed.count +
              ' material(s) in JSON, but this model-viewer instance reports ' +
              viewerN +
              '. If you analyzed a different file in gltf.report, re-download from this URL. If counts still differ, try re-export without rare extensions (e.g. test without KHR_materials_specular) or update the theme / report to Shopify (bundled model-viewer may parse fewer materials on error).'
          );
        } else if (parsed && parsed.count === viewerN) {
          console.info('Counts match — targeting by name or materialIndex in metafields should work once names load (ensureLoaded).');
        }
        console.groupEnd();
      })
      .catch(function (e) {
        console.warn(
          '[s3d-material-configurator] Could not fetch GLB for JSON compare (CORS/size/network):',
          abs,
          e && e.message ? e.message : e
        );
      });
  }

  /**
   * Lists every in-section <model-viewer>: src URL, load state, model.materials.length.
   * Optionally fetches src and parses GLB JSON materials[] to detect CDN vs viewer mismatch.
   */
  function auditSectionModelViewers(sectionId, modalSelector) {
    if (!s3dDebugEnabled()) return;
    var list = gatherModelViewersInSection(sectionId, modalSelector);
    console.group('[s3d-material-configurator] Model-viewer audit (section ' + sectionId + ')');
    console.info(
      'model.materials.length follows glTF "materials"[] in the loaded scene. Multiple meshes can share one material; your file may still have several materials — see "Live GLB vs model-viewer" below.'
    );
    if (!list.length) {
      console.info('No <model-viewer> in section/modal yet (open the 3D poster first).');
    }
    var i;
    var mv;
    for (i = 0; i < list.length; i++) {
      mv = list[i];
      var src = mv.getAttribute && mv.getAttribute('src');
      var ios = mv.getAttribute && mv.getAttribute('ios-src');
      var loaded = !!mv.loaded;
      var n = materialCountOnViewer(mv);
      var names =
        mv.model && mv.model.materials
          ? mv.model.materials.map(function (m, j) {
              return j + ':' + JSON.stringify(m && m.name != null ? m.name : '');
            })
          : [];
      console.info('viewer[' + i + ']', {
        loaded: loaded,
        hasModel: !!mv.model,
        materialCount: n,
        src: src,
        iosSrc: ios,
        materialNames: names.length ? names.join(', ') : '(none)',
        connected: mv.isConnected,
      });
    }
    console.groupEnd();
    for (i = 0; i < list.length; i++) {
      mv = list[i];
      var srcAttr = mv.getAttribute && mv.getAttribute('src');
      if (srcAttr) compareRemoteGlbMaterialsToViewer(mv, srcAttr, i);
    }
  }

  /** Helps when glTF uses extreme metal/roughness so base color reads as black. */
  function softenPbrForVisibility(pbr) {
    if (!pbr) return;
    try {
      if (typeof pbr.setMetallicFactor === 'function') pbr.setMetallicFactor(0);
      else if ('metallicFactor' in pbr && typeof pbr.metallicFactor === 'number') pbr.metallicFactor = 0;
      if (typeof pbr.setRoughnessFactor === 'function') pbr.setRoughnessFactor(0.85);
      else if ('roughnessFactor' in pbr && typeof pbr.roughnessFactor === 'number') pbr.roughnessFactor = 0.85;
    } catch (ignore) {
      /* optional API on some model-viewer builds */
    }
  }

  /**
   * model-viewer setBaseColorFactor expects linear RGBA in [0,1] (see glTF + model-viewer docs).
   * Never pass raw "#rrggbb" as the first attempt — some builds coerce it incorrectly → black mesh.
   * Accepts hex string, or number[] (linear 0–1 or sRGB 0–255 if any channel > 1).
   */
  function resolveLinearBaseColorFactor(colorCssOrHex) {
    if (colorCssOrHex == null || colorCssOrHex === '') {
      return [1, 1, 1, 1];
    }
    if (Array.isArray(colorCssOrHex) && colorCssOrHex.length >= 3) {
      var r0 = Number(colorCssOrHex[0]);
      var g0 = Number(colorCssOrHex[1]);
      var b0 = Number(colorCssOrHex[2]);
      var a0 = colorCssOrHex.length >= 4 ? Number(colorCssOrHex[3]) : 1;
      if (isNaN(r0) || isNaN(g0) || isNaN(b0)) return null;
      if (r0 > 1 || g0 > 1 || b0 > 1 || a0 > 1) {
        function lin255(c) {
          c /= 255;
          return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        }
        return [lin255(r0), lin255(g0), lin255(b0), a0 > 1 ? lin255(a0) : a0];
      }
      return [r0, g0, b0, isNaN(a0) ? 1 : a0];
    }
    var fromHex = hexToLinearRgba(String(colorCssOrHex).trim());
    if (fromHex) return fromHex;
    return null;
  }

  function applyBaseColor(materials, colorCssOrHex) {
    var factorArr = resolveLinearBaseColorFactor(colorCssOrHex);
    materials.forEach(function (mat) {
      try {
        var pbr = mat && mat.pbrMetallicRoughness;
        if (!pbr || typeof pbr.setBaseColorFactor !== 'function') {
          if (s3dDebugEnabled()) {
            console.warn(
              '[s3d-material-configurator] applyBaseColor: no pbrMetallicRoughness (e.g. unlit-only). Material:',
              mat && mat.name
            );
          }
          return;
        }
        softenPbrForVisibility(pbr);
        if (factorArr != null) {
          pbr.setBaseColorFactor(factorArr);
        } else {
          pbr.setBaseColorFactor(colorCssOrHex);
        }
      } catch (e) {
        console.warn('[s3d-material-configurator] setBaseColorFactor', mat && mat.name, e);
      }
    });
  }

  async function clearBaseColorTexture(materials) {
    await ensureMaterialsReady(materials);
    for (var i = 0; i < materials.length; i++) {
      try {
        var pbr = materials[i].pbrMetallicRoughness;
        if (!pbr || !pbr.baseColorTexture || typeof pbr.baseColorTexture.setTexture !== 'function') continue;
        await pbr.baseColorTexture.setTexture(null);
      } catch (e) {
        /* ignore */
      }
    }
  }

  /** @returns {number} how many materials received the texture (0 if GLB has no baseColorTexture slot). */
  async function applyBaseColorTexture(modelViewer, materials, url, tiling) {
    if (!url || !materials.length) return 0;
    await ensureMaterialsReady(materials);
    var repeatU =
      tiling && tiling.u != null ? tiling.u : S3D_DEFAULT_FABRIC_TEXTURE_REPEAT_U;
    var repeatV =
      tiling && tiling.v != null ? tiling.v : S3D_DEFAULT_FABRIC_TEXTURE_REPEAT_V;
    var tex;
    try {
      tex = await modelViewer.createTexture(url);
    } catch (e) {
      console.warn('[s3d-material-configurator] createTexture failed', url, e);
      return 0;
    }
    var applied = 0;
    var i;
    for (i = 0; i < materials.length; i++) {
      try {
        var pbr = materials[i].pbrMetallicRoughness;
        if (!pbr || !pbr.baseColorTexture || typeof pbr.baseColorTexture.setTexture !== 'function') continue;
        await pbr.baseColorTexture.setTexture(tex);
        /* setTexture copies the slot's stored transform (often 1×1) onto the map — tile after each bind. */
        configureMaterialsFabricTiling([materials[i]], repeatU, repeatV);
        applied++;
      } catch (e2) {
        console.warn('[s3d-material-configurator] setTexture', e2);
      }
    }
    if (applied > 0 && tex) {
      configureFabricTextureTiling(tex, repeatU, repeatV);
      configureMaterialsFabricTiling(materials, repeatU, repeatV);
      if (s3dDebugEnabled() && tex.sampler && tex.sampler.scale) {
        s3dTrace('applyBaseColorTexture: sampler.scale after tiling', {
          requested: { u: repeatU, v: repeatV },
          actual: tex.sampler.scale,
        });
      }
      if (modelViewer && typeof modelViewer.updateFraming === 'function') {
        try {
          modelViewer.updateFraming();
        } catch (ignoreFraming) {}
      }
    }
    if (applied === 0 && modelViewer) {
      if (!warnedNoBaseColorTextureSlot || !warnedNoBaseColorTextureSlot.has(modelViewer)) {
        if (warnedNoBaseColorTextureSlot) warnedNoBaseColorTextureSlot.add(modelViewer);
        console.warn(
          '[s3d-material-configurator] Could not attach fabric texture — glTF material has no baseColorTexture slot (common on single-shader exports). Re-export GLB with a base color texture slot on the fabric material.'
        );
      }
    }
    return applied;
  }

  function modelViewerLooksUsable(mv) {
    return mv && mv.loaded && mv.model;
  }

  function materialCountOnViewer(mv) {
    try {
      return mv && mv.model && mv.model.materials ? mv.model.materials.length : 0;
    } catch (e) {
      return 0;
    }
  }

  function namedMaterialCountOnViewer(mv) {
    try {
      var list = mv && mv.model && mv.model.materials ? mv.model.materials : [];
      var n = 0;
      var i;
      for (i = 0; i < list.length; i++) {
        if (list[i] && String(list[i].name || '').trim()) n++;
      }
      return n;
    } catch (e) {
      return 0;
    }
  }

  /**
   * Product galleries / modals often leave several <model-viewer> nodes in the DOM. A hidden or
   * stale instance can report 1 material while the visible GLB has many (same file in modelviewer.dev/editor).
   * When multiple loaded viewers exist, keep only those with the highest material count.
   */
  function prioritizePrimaryModelViewers(viewers) {
    if (!viewers || viewers.length <= 1) return viewers || [];
    var maxMats = 0;
    var i;
    var n;
    for (i = 0; i < viewers.length; i++) {
      n = materialCountOnViewer(viewers[i]);
      if (n > maxMats) maxMats = n;
    }
    if (maxMats <= 1) {
      var maxNamed = 0;
      for (i = 0; i < viewers.length; i++) {
        n = namedMaterialCountOnViewer(viewers[i]);
        if (n > maxNamed) maxNamed = n;
      }
      if (maxNamed > 0) {
        var namedPick = [];
        for (i = 0; i < viewers.length; i++) {
          if (namedMaterialCountOnViewer(viewers[i]) === maxNamed) namedPick.push(viewers[i]);
        }
        if (namedPick.length) {
          if (s3dDebugEnabled() && namedPick.length < viewers.length) {
            console.info(
              '[s3d-material-configurator] DEBUG: Preferring',
              namedPick.length,
              'viewer(s) with named materials over',
              viewers.length - namedPick.length,
              'duplicate(s) with unnamed materials.'
            );
          }
          return namedPick;
        }
      }
      return viewers;
    }
    var out = [];
    for (i = 0; i < viewers.length; i++) {
      if (materialCountOnViewer(viewers[i]) === maxMats) out.push(viewers[i]);
    }
    if (s3dDebugEnabled() && out.length < viewers.length) {
      console.info(
        '[s3d-material-configurator] DEBUG: Ignoring',
        viewers.length - out.length,
        '<model-viewer>(s) with fewer than',
        maxMats,
        'materials (gallery/modal duplicate).'
      );
    }
    return out.length ? out : viewers;
  }

  /**
   * Gallery modals are often outside the shopify-section subtree (e.g. reparented) or missed by
   * subtree queries. Scan all <model-viewer> in the document and keep this product's section,
   * #MainProduct-{id}, and the gallery modal.
   */
  function viewerBelongsToSection(mv, sectionId, modalSelector) {
    if (!mv || sectionId == null || sectionId === '') return false;
    var sid = String(sectionId);
    var secEl = document.getElementById('shopify-section-' + sid);
    if (secEl && secEl.contains(mv)) return true;
    var mainEl = document.getElementById('MainProduct-' + sid);
    if (mainEl && mainEl.contains(mv)) return true;
    var ms = (modalSelector || '').trim();
    if (ms) {
      var modalRoot = document.querySelector(ms);
      if (modalRoot && modalRoot.contains(mv)) return true;
    }
    return false;
  }

  function gatherModelViewersInSection(sectionId, modalSelector) {
    var all = document.querySelectorAll('model-viewer');
    var out = [];
    var seen = new Set();
    var i;
    var mv;
    for (i = 0; i < all.length; i++) {
      mv = all[i];
      if (seen.has(mv)) continue;
      if (!viewerBelongsToSection(mv, sectionId, modalSelector)) continue;
      seen.add(mv);
      out.push(mv);
    }
    return out;
  }

  function findLoadedModelViewersFromGather(gatherFn) {
    var candidates = gatherFn();
    var seenMv = new Set();
    var out = [];
    var i;
    var mv;
    for (i = 0; i < candidates.length; i++) {
      mv = candidates[i];
      if (seenMv.has(mv)) continue;
      seenMv.add(mv);
      if (modelViewerLooksUsable(mv)) out.push(mv);
    }
    out.sort(function (a, b) {
      var dc = materialCountOnViewer(b) - materialCountOnViewer(a);
      if (dc !== 0) return dc;
      return namedMaterialCountOnViewer(b) - namedMaterialCountOnViewer(a);
    });
    return out;
  }

  function bindDirectModelViewerGather(gatherFn, handler) {
    var list = gatherFn();
    var j;
    var mv;
    for (j = 0; j < list.length; j++) {
      mv = list[j];
      if (viewerGraphDirectBound.has(mv)) continue;
      viewerGraphDirectBound.add(mv);
      mv.addEventListener('load', handler);
      mv.addEventListener('scene-graph-ready', handler);
    }
  }

  /**
   * Wait until model-viewer exposes mv.model (user may click leg color before GLB finishes).
   */
  function waitForViewerModel(mv, maxWaitMs) {
    if (!mv) return Promise.resolve(false);
    if (mv.model) return Promise.resolve(true);
    return new Promise(function (resolve) {
      var done = false;
      function finish(ok) {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve(ok);
      }
      var timer = setTimeout(function () {
        finish(false);
      }, maxWaitMs || 12000);
      function onReady() {
        finish(!!mv.model);
      }
      mv.addEventListener('load', onReady, { once: true });
      mv.addEventListener('scene-graph-ready', onReady, { once: true });
    });
  }

  /**
   * Resolve viewers for mutations: all loaded instances in the section (fixes wrong hidden slide),
   * or wait for the first viewer if the user clicked before the GLB attached.
   */
  async function resolveModelViewersForApply(viewerCtx) {
    var gather = viewerCtx.gather;
    var warnAnchor = viewerCtx.warnAnchor;
    var rootSel = viewerCtx.rootSel;
    var rawGathered = gather();
    s3dTrace('resolveViewers: gathered <model-viewer> nodes in section', {
      count: rawGathered.length,
      loadedPrioritize: findLoadedModelViewersFromGather(gather).length,
    });
    var viewers = prioritizePrimaryModelViewers(findLoadedModelViewersFromGather(gather));
    if (viewers.length) {
      s3dTrace('resolveViewers: using loaded viewers', { count: viewers.length });
      return viewers;
    }
    var candidates = gather();
    var mv = candidates.length ? candidates[0] : null;
    if (mv) {
      await waitForViewerModel(mv, 12000);
      viewers = prioritizePrimaryModelViewers(findLoadedModelViewersFromGather(gather));
      if (viewers.length) return viewers;
      if (mv.model) return prioritizePrimaryModelViewers([mv]);
    }
    if (!warnedNoModelRoots || !warnedNoModelRoots.has(warnAnchor)) {
      if (warnedNoModelRoots) warnedNoModelRoots.add(warnAnchor);
      console.warn(
        '[s3d-material-configurator] No model with scene graph yet. Open the 3D poster once, or check the section:',
        rootSel || ''
      );
    }
    s3dTrace('resolveViewers: none ready (waited / no model)', { rootSel: rootSel });
    return [];
  }

  function buildUIMount(mount, fabricsData, legsData, modelsData, headings) {
    mount.innerHTML = '';
    var modelKeys = Object.keys(modelsData || {});
    var fabricKeys = Object.keys(fabricsData || {});
    var legKeys = Object.keys(legsData || {});

    // Model switching is now driven by native Shopify variant selection —
    // no custom UI buttons are rendered here.

    if (fabricKeys.length) {
      // Fabric selector buttons — rendered for 1 or more fabrics so the fabric
      // name is always visible; clicking switches fabric + color swatches.
      if (fabricKeys.length >= 1) {
        var hFab = document.createElement('p');
        hFab.className = 's3d-mat-config__heading';
        hFab.textContent = headings.fabrics;
        mount.appendChild(hFab);

        var rowFab = document.createElement('div');
        rowFab.className = 's3d-mat-config__row';
        rowFab.setAttribute('role', 'group');
        rowFab.setAttribute('aria-label', headings.fabrics);
        fabricKeys.forEach(function (fkey, fidx) {
          var entry = fabricsData[fkey];
          var btn = document.createElement('button');
          btn.type = 'button';
          // No 'is-active' on init — the flow controller requires an explicit click
          // before the fabric step is considered "complete" and the color step appears.
          btn.className = 's3d-mat-config__btn';
          btn.setAttribute('data-s3d-fabric', fkey);
          btn.textContent = (entry && entry.label) || fkey;
          rowFab.appendChild(btn);
        });
        mount.appendChild(rowFab);
      }

      // One color-swatch section per fabric; only the first is visible on load
      fabricKeys.forEach(function (fkey, fidx) {
        var entry = fabricsData[fkey];
        if (!entry || !entry.colors || typeof entry.colors !== 'object') return;
        var colorKeys = Object.keys(entry.colors);
        if (!colorKeys.length) return;

        var colorSection = document.createElement('div');
        colorSection.setAttribute('data-s3d-color-section', fkey);
        // All color sections start hidden — the fabric click handler shows the
        // one matching the chosen fabric. The flow controller wraps all of these
        // into a single [data-flow-step="color"] container that is hidden until
        // the fabric step is interacted with.
        colorSection.style.display = 'none';

        // Color heading + selected-color name (updated on click)
        var hCol = document.createElement('p');
        hCol.className = 's3d-mat-config__heading';
        hCol.textContent = headings.colors + ': ';
        var colorNameEl = document.createElement('span');
        colorNameEl.className = 's3d-mat-config__color-name';
        colorNameEl.setAttribute('data-s3d-color-name', fkey);
        hCol.appendChild(colorNameEl);
        colorSection.appendChild(hCol);

        // Swatch grid — uses a dedicated class so the flow controller's CSS
        // (which hides .s3d-mat-config__row for dropdown conversion) does NOT hide it.
        var rowCol = document.createElement('div');
        rowCol.className = 's3d-mat-config__swatch-row';
        rowCol.setAttribute('role', 'group');
        rowCol.setAttribute('aria-label', headings.colors);
        colorKeys.forEach(function (ckey) {
          var swatchUrl = resolveFabricTextureUrl(entry, ckey);

          var sw = document.createElement('button');
          sw.type = 'button';
          sw.className = 's3d-mat-config__swatch-btn'; // no is-active on init
          sw.setAttribute('data-s3d-color', ckey);
          sw.setAttribute('data-s3d-fabric-ref', fkey);
          sw.title = ckey;
          sw.setAttribute('aria-label', ckey);

          if (swatchUrl) {
            var img = document.createElement('img');
            img.src = swatchUrl;
            img.alt = ckey;
            img.loading = 'lazy';
            sw.appendChild(img);
          } else {
            sw.style.backgroundColor = '#e8e4df';
          }
          rowCol.appendChild(sw);
        });
        colorSection.appendChild(rowCol);
        mount.appendChild(colorSection);
      });
    }

    if (legKeys.length) {
      var legSection = document.createElement('div');
      legSection.setAttribute('data-s3d-leg-section', '');

      var hLeg = document.createElement('p');
      hLeg.className = 's3d-mat-config__heading';
      hLeg.textContent = headings.legs;
      legSection.appendChild(hLeg);

      var rowLeg = document.createElement('div');
      rowLeg.className = 's3d-mat-config__row';
      rowLeg.setAttribute('role', 'group');
      rowLeg.setAttribute('aria-label', headings.legs);
      legKeys.forEach(function (key, idx) {
        var entry = legsData[key];
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 's3d-mat-config__btn' + (idx === 0 ? ' is-active' : '');
        btn.setAttribute('data-s3d-leg', key);
        btn.textContent = (entry && entry.label) || key;
        rowLeg.appendChild(btn);
      });
      legSection.appendChild(rowLeg);
      mount.appendChild(legSection);
    }
  }

  function fabricButtons(wrap) {
    return wrap.querySelectorAll('.s3d-mat-config__btn[data-s3d-fabric]');
  }

  function initConfigurator(wrap) {
    var sectionIdEarly = wrap && wrap.getAttribute ? wrap.getAttribute('data-section-id') : '';
    if (wrap.getAttribute('data-s3d-initialized') === '1') {
      s3dTrace('init:skip already initialized', { sectionId: sectionIdEarly });
      return;
    }

    var sectionId = wrap.getAttribute('data-section-id');
    var blockId = wrap.getAttribute('data-block-id') || '';
    var jsonSuf = blockId ? sectionId + '-' + blockId : sectionId;
    var rootSel = wrap.getAttribute('data-root-selector') || '#shopify-section-' + sectionId;
    var rootBySel = document.querySelector(rootSel);
    var mainProductEl = document.querySelector('#MainProduct-' + sectionId);
    var closestSection = wrap.closest('[id^="shopify-section-"]');
    var closestJsProduct = wrap.closest('.js-product-wrapper');
    var galleryModalSel = (wrap.getAttribute('data-gallery-modal-selector') || '').trim();
    var modalSel = galleryModalSel || '#product-detail-modal';
    var anchorRoot = closestSection || rootBySel || mainProductEl || closestJsProduct;
    if (!anchorRoot) {
      s3dTrace('init:abort no anchorRoot', {
        sectionId: sectionId,
        rootSel: rootSel,
        hasClosestSection: !!closestSection,
        hasRootBySel: !!rootBySel,
        hasMainProductEl: !!mainProductEl,
        hasClosestJsProduct: !!closestJsProduct,
      });
      return;
    }

    function gatherProductModelViewers() {
      return gatherModelViewersInSection(sectionId, modalSel);
    }

    var viewerCtx = {
      gather: gatherProductModelViewers,
      warnAnchor: anchorRoot,
      rootSel: rootSel,
    };

    var allowSingleMaterialFallback = wrap.getAttribute('data-s3d-single-material-fallback') !== 'false';
    var matEntryOpts = { allowSingleMaterialFallback: allowSingleMaterialFallback };

    /**
     * Block-level material index/name overrides.
     * data-s3d-fabric-material-index → force fabric + color buttons to target this glTF index (0 = Grey_Mat_1001).
     * data-s3d-fabric-material-name  → force by name when no index is set.
     * data-s3d-leg-material-index    → force leg buttons to target this glTF index (2 = Wood_1001).
     * These win over whatever material/materialIndex the metafield entries carry.
     */
    var fabricBlockMaterialIndex = null;
    var fabricBlockMaterialName = null;
    var legBlockMaterialIndex = null;

    var rawFabIdx = wrap.getAttribute('data-s3d-fabric-material-index');
    if (rawFabIdx != null && rawFabIdx.trim() !== '') {
      var parsedFabIdx = parseInt(rawFabIdx.trim(), 10);
      if (!isNaN(parsedFabIdx) && parsedFabIdx >= 0) fabricBlockMaterialIndex = parsedFabIdx;
    }
    var rawFabName = wrap.getAttribute('data-s3d-fabric-material-name');
    if (rawFabName && rawFabName.trim()) fabricBlockMaterialName = rawFabName.trim();

    var rawLegIdx = wrap.getAttribute('data-s3d-leg-material-index');
    if (rawLegIdx != null && rawLegIdx.trim() !== '') {
      var parsedLegIdx = parseInt(rawLegIdx.trim(), 10);
      if (!isNaN(parsedLegIdx) && parsedLegIdx >= 0) legBlockMaterialIndex = parsedLegIdx;
    }

    s3dTrace('init:blockOverrides', {
      fabricBlockMaterialIndex: fabricBlockMaterialIndex,
      fabricBlockMaterialName: fabricBlockMaterialName,
      legBlockMaterialIndex: legBlockMaterialIndex,
      fabricTextureRepeatDefault: {
        u: S3D_DEFAULT_FABRIC_TEXTURE_REPEAT_U,
        v: S3D_DEFAULT_FABRIC_TEXTURE_REPEAT_V,
      },
    });

    var fabricsData = coerceConfigObject(parseJsonScript('s3d-data-fabrics-' + jsonSuf));
    var legsData = coerceConfigObject(parseJsonScript('s3d-data-legs-' + jsonSuf));
    var modelsData = coerceConfigObject(parseJsonScript('s3d-data-models-' + jsonSuf));
    var fabricKeys = Object.keys(fabricsData);
    var legKeys = Object.keys(legsData);
    var modelKeys = Object.keys(modelsData);
    if (!fabricKeys.length && !legKeys.length && !modelKeys.length) {
      // Merchant-facing notice: kept in the console (not on the page) so customers never
      // see a raw "add metafields" message. Logs once per block even without ?s3d_debug=1.
      console.warn(
        '[s3d-material-configurator] No 3D options configured for this product (section ' +
          sectionId +
          (blockId ? ', block ' + blockId : '') +
          '). Add JSON metafields custom.fabrics, custom.leg, and/or custom.model_variants to enable 3D options.'
      );
      s3dTrace('init:abort empty metafield JSON (no fabric, leg, or model keys)', {
        sectionId: sectionId,
        blockId: blockId,
        fabricsDataKeys: fabricKeys.length,
        legsDataKeys: legKeys.length,
        modelsDataKeys: modelKeys.length,
      });
      return;
    }

    s3dTrace('init:start', {
      sectionId: sectionId,
      blockId: blockId,
      fabricKeys: fabricKeys,
      legKeys: legKeys,
      modelKeys: modelKeys,
      modalSel: modalSel,
    });

    wrap.setAttribute('data-s3d-initialized', '1');

    if (s3dDebugEnabled() && typeof window !== 'undefined' && !window.__S3D_PREP_TRACE_DONE__) {
      window.__S3D_PREP_TRACE_DONE__ = true;
      console.info('[s3d-material-configurator] GLB preparation & metafield targeting — see', window.__S3D_GLB_PREP__);
    }

    var mount = wrap.querySelector('[data-s3d-ui-mount]');
    if (mount && (fabricKeys.length || legKeys.length || modelKeys.length)) {
      buildUIMount(mount, fabricsData, legsData, modelsData, {
        models: wrap.getAttribute('data-heading-models') || 'Frame type',
        fabrics: wrap.getAttribute('data-heading-fabrics') || 'Fabric',
        colors: wrap.getAttribute('data-heading-colors') || 'Color',
        legs: wrap.getAttribute('data-heading-legs') || 'Leg finish',
      });
    }

    var state = {
      modelKey: modelKeys[0] || null,
      /** No fabric/colour apply until the user selects a fabric (avoid first-paint overrides). */
      fabricKey: null,
      colorKey: null,
      legKey: legKeys[0] || null,
    };

    function firstColorKey(fkey) {
      var f = fabricsData[fkey];
      if (!f || !f.colors || typeof f.colors !== 'object') return null;
      var ck = Object.keys(f.colors);
      return ck[0] || null;
    }

    // Initial load should not apply any fabric/color automatically.
    // state.fabricKey and state.colorKey stay null until first user interaction.

    // Set initial leg-section visibility based on the default model variant
    if (mount && state.modelKey && modelsData[state.modelKey]) {
      var initialLegSection = mount.querySelector('[data-s3d-leg-section]');
      if (initialLegSection) {
        initialLegSection.style.display = modelsData[state.modelKey].has_legs !== false ? '' : 'none';
      }
    }

    /** One in-flight apply at a time so scheduleApplyAll + clicks do not interleave fabric/legs. */
    var applyChain = Promise.resolve();
    function enqueueApply(asyncFn) {
      applyChain = applyChain.then(asyncFn).catch(function (e) {
        console.warn('[s3d-material-configurator] apply', e);
      });
      return applyChain;
    }

    function warnNoMaterialsMatched(kind, spec, model) {
      var cfg =
        spec && typeof spec === 'object' && !Array.isArray(spec)
          ? spec
          : { names: spec };
      var warnKey = kind + '|' + JSON.stringify(cfg);
      if (model && !s3dShouldEmitModelWarning(model, warnKey)) return;
      var indexNameLine =
        model && model.materials && model.materials.length
          ? model.materials
              .map(function (m, i) {
                return '[' + i + ']=' + JSON.stringify((m && m.name) != null ? m.name : '');
              })
              .join(', ')
          : '(no model.materials)';
      console.warn(
        '[s3d-material-configurator] No materials matched for ' + kind + '.',
        'Configured:',
        cfg
      );
      console.warn('[s3d-material-configurator] Actual glTF materials on this model:', indexNameLine);
      if (model && model.materials && model.materials.length) {
        console.table(
          model.materials.map(function (m, i) {
            return { index: i, name: (m && m.name) || '(unnamed)' };
          })
        );
        if (model.materials.length === 1) {
          console.warn(
            '[s3d-material-configurator] Only one material in the file — headboard and legs cannot use different colors until you assign multiple materials in Blender (or use materialIndex: 0 and accept one shared look).'
          );
        } else {
          console.info(
            '[s3d-material-configurator] Align metafield "material" with a name above, or set "materialIndex". https://modelviewer.dev/examples/scenegraph/'
          );
        }
      }
    }

    async function applyFabricAndColor() {
      var fk = state.fabricKey;
      s3dTrace('applyFabricAndColor:step1 state', { fabricKey: fk, colorKey: state.colorKey });
      if (!fk || !fabricsData[fk]) {
        s3dTrace('applyFabricAndColor:abort no fabric key or entry', { fabricKey: fk });
        return;
      }

      var entry = fabricsData[fk];
      var textureUrl = resolveFabricTextureUrl(entry, state.colorKey);

      s3dTrace('applyFabricAndColor:step2 resolved texture', { textureUrl: textureUrl || null });

      if (!textureUrl) {
        s3dTrace('applyFabricAndColor:abort no valid texture URL for color', { colorKey: state.colorKey });
        return;
      }

      var viewers = await resolveModelViewersForApply(viewerCtx);
      if (!viewers.length) {
        s3dTrace('applyFabricAndColor:abort no model-viewer with scene graph');
        return;
      }

      var vi;
      var applied = false;
      for (vi = 0; vi < viewers.length; vi++) {
        var mv = viewers[vi];
        var model = mv.model;
        await ensureAllModelMaterialsLoaded(model);
        if (!model.materials || model.materials.length <= 1) {
          await waitForModelMaterialsStabilize(model, 1400);
        }
        await waitForMaterialCountIncrease(model, 1, 4000);
        s3dTrace('applyFabricAndColor:step3 viewer', {
          index: vi,
          modelMaterialCount: model.materials ? model.materials.length : 0,
          materialNames:
            model.materials &&
            model.materials.map(function (m, i) {
              return i + ':' + (m && m.name != null ? m.name : '');
            }),
        });

        var mats;
        if (fabricBlockMaterialIndex != null) {
          /* Block-level index override (data-s3d-fabric-material-index) — targets Grey_Mat_1001 (index 0). */
          mats = materialsByGltfIndex(model, [fabricBlockMaterialIndex]);
          s3dTrace('applyFabricAndColor:step4 blockIndexOverride', {
            index: fabricBlockMaterialIndex,
            matchedCount: mats.length,
          });
        } else if (fabricBlockMaterialName) {
          /* Block-level name override (data-s3d-fabric-material-name). */
          mats = materialsForTargets(model, [fabricBlockMaterialName]);
          s3dTrace('applyFabricAndColor:step4 blockNameOverride', {
            name: fabricBlockMaterialName,
            matchedCount: mats.length,
          });
        } else if (hasAnyMaterialSelector(entry)) {
          /* Metafield entry has explicit material / materialIndex targeting — use it. */
          mats = materialsForEntry(model, entry, matEntryOpts);
          s3dTrace('applyFabricAndColor:step4 metafieldEntry', {
            matchedCount: mats.length,
            targets: resolveMaterialTargetField(entry),
            indices: resolveMaterialIndices(entry),
          });
        } else {
          mats = [];
        }

        /*
         * Default fallback: when nothing else resolved, target materials[0] (Grey_Mat_1001).
         * This is the headboard + bedframe fabric material — fabric/color buttons must never
         * spill onto the mattress (index 1) or the leg wood (index 2).
         */
        if (!mats.length) {
          var list = model.materials || [];
          if (list.length > 0 && list[0]) {
            mats = [list[0]];
            s3dTrace('applyFabricAndColor:step4 defaultFallback materials[0]', { name: list[0].name });
          }
        }

        if (!mats.length) {
          warnNoMaterialsMatched(
            'fabric',
            {
              names: resolveMaterialTargetField(entry),
              indices: resolveMaterialIndices(entry),
            },
            model
          );
          continue;
        }
        mats = expandToBlenderNumberedVariants(model, mats);
        await ensureMaterialsReady(mats);
        await clearBaseColorTexture(mats);
        resetFabricMaterialBaseFactor(mats);
        var tiling = resolveFabricTextureRepeat(entry, state.colorKey);
        s3dTrace('applyFabricAndColor:step4b texture tiling', {
          fabricKey: fk,
          colorKey: state.colorKey,
          resolved: tiling,
        });
        await applyBaseColorTexture(mv, mats, textureUrl, tiling);
        applied = true;
        s3dTrace('applyFabricAndColor:step5 applied to viewer', { index: vi, tiling: tiling });
      }
      if (!applied) {
        s3dTrace('applyFabricAndColor:done nothing applied (check material name / index warnings above)');
      } else {
        s3dTrace('applyFabricAndColor:done success', { viewersUpdated: viewers.length, applied: applied });
      }
    }

    /**
     * The "Feet colour" selector (custom.feet_selector metafield, handled by the separate
     * flow-controller script in 3d-main-product.liquid) targets its own material by name —
     * defaulting to "Wood_1001" when the metafield doesn't set one. If that selector is also
     * present on this product, its target name must NOT be guessed by the leg name-fallback
     * below, otherwise both UI controls would silently recolor the same glTF material and
     * fighting/duplicate updates would result. Only returns a name when the feet-selector
     * fieldset actually exists in the DOM (i.e. custom.feet_selector is set on this product).
     */
    function getFeetSelectorMaterialName() {
      try {
        var feetSelectorEl = document.getElementById('feet-selector-' + sectionId);
        if (!feetSelectorEl) return '';
        var cfgEl = document.getElementById('s3d-flow-config-' + sectionId);
        if (!cfgEl) return '';
        var cfg = JSON.parse(cfgEl.textContent);
        return cfg && cfg.feetMaterial ? String(cfg.feetMaterial).trim() : '';
      } catch (e) {
        return '';
      }
    }

    async function applyLegs() {
      var lk = state.legKey;
      s3dTrace('applyLegs:step1 state', { legKey: lk });
      if (!lk || !legsData[lk]) {
        s3dTrace('applyLegs:abort no leg key or entry');
        return;
      }
      var entry = legsData[lk];

      var hex = flattenTargetToken(entry.color) || '#ffffff';
      s3dTrace('applyLegs:step2 color', { hex: hex });

      var viewers = await resolveModelViewersForApply(viewerCtx);
      if (!viewers.length) {
        s3dTrace('applyLegs:abort no model-viewer');
        return;
      }

      var vi;
      var applied = false;
      for (vi = 0; vi < viewers.length; vi++) {
        var model = viewers[vi].model;
        await ensureAllModelMaterialsLoaded(model);
        if (!model.materials || model.materials.length <= 1) {
          await waitForModelMaterialsStabilize(model, 1400);
        }
        await waitForMaterialCountIncrease(model, 1, 4000);

        var mats;
        if (legBlockMaterialIndex != null) {
          /* Block-level index override (data-s3d-leg-material-index) — targets Wood_1001 (index 2). */
          mats = materialsByGltfIndex(model, [legBlockMaterialIndex]);
          s3dTrace('applyLegs:step3 blockIndexOverride', {
            index: legBlockMaterialIndex,
            matchedCount: mats.length,
          });
        } else if (hasAnyMaterialSelector(entry)) {
          /* Metafield entry has explicit material / materialIndex targeting — use it. */
          mats = materialsForEntry(model, entry, matEntryOpts);
          s3dTrace('applyLegs:step3 metafieldEntry', {
            matchedCount: mats.length,
            targets: resolveMaterialTargetField(entry),
            indices: resolveMaterialIndices(entry),
          });
        } else {
          mats = [];
        }

        /*
         * Name-based fallback: try common leg/wood material names (Wood_1001, Wood, Leg,
         * Legs, …) — matched case-insensitively and including Blender duplicate suffixes
         * (Wood_1001.001, Wood_1001.009, etc.) — before resorting to a blind index guess.
         * This catches GLBs where the leg material isn't at a fixed index but IS named
         * conventionally.
         *
         * Safety: if this product ALSO has a "Feet colour" selector (custom.feet_selector)
         * targeting its own material by name, that exact name is excluded from the candidate
         * list here so the two independent selectors can never silently fight over the same
         * glTF material. A console notice is logged (ungated) whenever this fallback matches,
         * so a merchant can catch an incorrect guess and pin it down with an explicit
         * "material" / "materialIndex" in the custom.leg metafield entry instead.
         */
        if (!mats.length) {
          var excludeName = getFeetSelectorMaterialName();
          var legNameCandidates = excludeName
            ? LEG_MATERIAL_NAME_FALLBACKS.filter(function (n) {
                return n.toLowerCase() !== excludeName.toLowerCase();
              })
            : LEG_MATERIAL_NAME_FALLBACKS;
          mats = materialsForTargets(model, legNameCandidates);
          if (mats.length) {
            console.info(
              '[s3d-material-configurator] Leg finish: no material/materialIndex set on this ' +
                'custom.leg entry — guessed by name match: ' +
                mats.map(function (m) { return m.name; }).join(', ') +
                '. If this is the wrong part, add an explicit "material" or "materialIndex" to ' +
                'the custom.leg metafield entry (see gltf.report for the correct index/name).'
            );
            s3dTrace('applyLegs:step3 nameFallback', {
              matchedCount: mats.length,
              names: mats.map(function (m) { return m.name; }),
              excludedFeetMaterial: excludeName,
            });
          }
        }

        /*
         * Default fallback: when nothing else resolved, target materials[2] (Wood_1001 — legs).
         * If the model has fewer than 3 materials, fall back to the last available slot.
         * Leg finish buttons must never touch the fabric (index 0) or mattress (index 1).
         */
        if (!mats.length) {
          var list = model.materials || [];
          var legDefaultIdx = list.length >= 3 ? 2 : list.length - 1;
          if (legDefaultIdx >= 0 && list[legDefaultIdx]) {
            mats = [list[legDefaultIdx]];
            s3dTrace('applyLegs:step3 defaultFallback', { index: legDefaultIdx, name: list[legDefaultIdx].name });
          }
        }

        if (!mats.length) {
          warnNoMaterialsMatched(
            'legs',
            {
              names: resolveMaterialTargetField(entry),
              indices: resolveMaterialIndices(entry),
            },
            model
          );
          continue;
        }
        mats = expandToBlenderNumberedVariants(model, mats);
        await ensureMaterialsReady(mats);
        /* Clear before tint so leg color is not multiplied by a dark fabric/base map. */
        await clearBaseColorTexture(mats);
        applyBaseColor(mats, hex);
        applied = true;
        s3dTrace('applyLegs:step4 applied', { index: vi });
      }
      if (!applied) {
        s3dTrace('applyLegs:done nothing applied');
      } else {
        s3dTrace('applyLegs:done success');
      }
    }

    async function applyInnerBoxColor() {
      // Inner box colour application has been disabled — GLB default colour is used as-is.
      return;
      /* eslint-disable no-unreachable */
      var materialName = String(wrap.getAttribute('data-s3d-inner-box-material-name') || '').trim();
      var colorHex = String(wrap.getAttribute('data-s3d-inner-box-color') || '').trim();
      if (!materialName) {
        s3dTrace('applyInnerBox:skip no material name');
        return;
      }

      function getOttomanBedFrameValues() {
        var values = [];
        modelKeys.forEach(function (mkey) {
          var entry = modelsData[mkey];
          if (!entry || entry.has_legs !== false) return;
          var kws = entry.variant_keywords || entry.variant_options || [];
          if (!Array.isArray(kws)) return;
          kws.forEach(function (kw) {
            var v = String(kw || '').trim();
            if (v) values.push(v);
          });
        });
        return values;
      }

      function getCurrentBedFrameValue() {
        var secEl = document.getElementById('shopify-section-' + sectionId) || anchorRoot;
        var fieldsets = secEl.querySelectorAll('fieldset');
        var fi;
        for (fi = 0; fi < fieldsets.length; fi++) {
          var legend = fieldsets[fi].querySelector('legend');
          if (!legend || !/bed frame/i.test(String(legend.textContent || '').trim())) continue;
          var checked = fieldsets[fi].querySelector('input[type="radio"]:checked');
          if (checked) return String(checked.value || '').trim();
          var first = fieldsets[fi].querySelector('input[type="radio"]');
          return first ? String(first.value || '').trim() : '';
        }
        return '';
      }

      function shouldApplyInnerBoxBeige() {
        var bedFrame = getCurrentBedFrameValue();
        var ottomanValues = getOttomanBedFrameValues();
        if (bedFrame) {
          return ottomanValues.indexOf(bedFrame) === -1;
        }
        if (state.modelKey && modelsData[state.modelKey]) {
          return modelsData[state.modelKey].has_legs !== false;
        }
        return true;
      }

      if (!shouldApplyInnerBoxBeige()) {
        s3dTrace('applyInnerBox:skip ottoman / storage frame — keep GLB default');
        return;
      }

      if (!colorHex) {
        s3dTrace('applyInnerBox:skip no color configured');
        return;
      }

      var viewers = await resolveModelViewersForApply(viewerCtx);
      if (!viewers.length) {
        s3dTrace('applyInnerBox:skip no model-viewer');
        return;
      }

      var vi;
      for (vi = 0; vi < viewers.length; vi++) {
        var model = viewers[vi].model;
        await ensureAllModelMaterialsLoaded(model);
        var mats = materialsForTargets(model, [materialName]);
        if (!mats.length) {
          s3dTrace('applyInnerBox:skip no material', { name: materialName, viewerIndex: vi });
          continue;
        }
        mats = expandToBlenderNumberedVariants(model, mats);
        await ensureMaterialsReady(mats);
        await clearBaseColorTexture(mats);
        applyBaseColor(mats, colorHex);
        s3dTrace('applyInnerBox:applied', { name: materialName, color: colorHex, viewerIndex: vi });
      }
    }

    async function applyAll() {
      s3dTrace('applyAll:start', { fabricKey: state.fabricKey, colorKey: state.colorKey, legKey: state.legKey });
      await applyFabricAndColor();
      await applyLegs();
      await applyInnerBoxColor();
      s3dTrace('applyAll:end');
    }

    /**
     * Apply metafield-driven materials once any model-viewer in the section has a scene graph.
     * Uses capture on the section so every <model-viewer> (e.g. multiple gallery slides) is covered.
     */
    var applyAllScheduled = null;
    function scheduleApplyAll() {
      if (applyAllScheduled) clearTimeout(applyAllScheduled);
      applyAllScheduled = setTimeout(function () {
        applyAllScheduled = null;
        var vws = findLoadedModelViewersFromGather(gatherProductModelViewers);
        s3dTrace('scheduleApplyAll:tick', {
          sectionId: sectionId,
          loadedWithSceneGraph: vws.length,
        });
        if (!vws.length) {
          s3dTrace('scheduleApplyAll:skip no loaded viewers yet');
          return;
        }
        if (s3dDebugEnabled()) {
          var gathered = gatherProductModelViewers();
          console.info(
            '[s3d-material-configurator] DEBUG: gathered',
            gathered.length,
            '<model-viewer> node(s) for section',
            sectionId,
            '(loaded with scene graph:',
            vws.length,
            ')'
          );
          vws.forEach(function (mv, idx) {
            logModelViewerSource(mv, 'applyAll viewer ' + idx);
          });
          if (!anchorRoot.__s3dModelAuditDone) {
            anchorRoot.__s3dModelAuditDone = true;
            auditSectionModelViewers(sectionId, modalSel);
          }
        }
        s3dTrace('scheduleApplyAll: invoking applyAll()');
        enqueueApply(function () {
          return applyAll();
        });
      }, 64);
    }

    function bindWhenModelViewerReady() {
      s3dTrace('bindWhenModelViewerReady', { sectionId: sectionId, anchorTag: anchorRoot && anchorRoot.tagName });
      anchorRoot.__s3dScheduleApplyFns = anchorRoot.__s3dScheduleApplyFns || [];
      anchorRoot.__s3dScheduleApplyFns.push(scheduleApplyAll);

      function dispatchModelViewerSchedules() {
        var fns = anchorRoot.__s3dScheduleApplyFns || [];
        var i;
        for (i = 0; i < fns.length; i++) {
          fns[i]();
        }
      }

      function scheduleStaggeredReapply() {
        if (anchorRoot.__s3dStagger1) clearTimeout(anchorRoot.__s3dStagger1);
        if (anchorRoot.__s3dStagger2) clearTimeout(anchorRoot.__s3dStagger2);
        anchorRoot.__s3dStagger1 = setTimeout(dispatchModelViewerSchedules, 500);
        anchorRoot.__s3dStagger2 = setTimeout(dispatchModelViewerSchedules, 1800);
      }

      function onViewerGraphDirect() {
        bindDirectModelViewerGather(gatherProductModelViewers, onViewerGraphDirect);
        // Apply a minimum roughness floor to every material so that near-mirror
        // materials (roughnessFactor < 0.15) cannot appear as pure black in the
        // neutral environment.  Only raises roughness; never lowers it.
        gatherProductModelViewers().forEach(function(mvEl) {
          var mdl = mvEl && mvEl.model;
          if (!mdl || !mdl.materials) return;
          mdl.materials.forEach(function(mat) {
            try {
              var pbr = mat && mat.pbrMetallicRoughness;
              if (!pbr) return;
              var roughness = typeof pbr.roughnessFactor === 'number' ? pbr.roughnessFactor : 1;
              if (roughness < 0.15 && typeof pbr.setRoughnessFactor === 'function') {
                pbr.setRoughnessFactor(0.15);
              }
            } catch (ignore) {}
          });
        });
        dispatchModelViewerSchedules();
        scheduleStaggeredReapply();
      }

      function onModelViewerGraphEvent(ev) {
        var t = ev.target;
        if (!t || t.tagName !== 'MODEL-VIEWER') return;
        if (!viewerBelongsToSection(t, sectionId, modalSel)) {
          s3dTrace('onModelViewerGraphEvent:ignored (viewer outside section)', {
            type: ev.type,
            sectionId: sectionId,
          });
          return;
        }
        s3dTrace('onModelViewerGraphEvent', { type: ev.type, sectionId: sectionId });
        bindDirectModelViewerGather(gatherProductModelViewers, onViewerGraphDirect);
        dispatchModelViewerSchedules();
        scheduleStaggeredReapply();
      }

      bindDirectModelViewerGather(gatherProductModelViewers, onViewerGraphDirect);

      if (anchorRoot.__s3dMvDelegationBound) {
        s3dTrace('bindWhenModelViewerReady: already bound, scheduleApplyAll only');
        scheduleApplyAll();
        return;
      }
      anchorRoot.__s3dMvDelegationBound = true;
      s3dTrace('bindWhenModelViewerReady: attaching scene-graph-ready + load delegation on anchor');

      anchorRoot.addEventListener('scene-graph-ready', onModelViewerGraphEvent, true);
      anchorRoot.addEventListener('load', onModelViewerGraphEvent, true);
      var mo = new MutationObserver(function () {
        bindDirectModelViewerGather(gatherProductModelViewers, onViewerGraphDirect);
        if (gatherProductModelViewers().length) dispatchModelViewerSchedules();
      });
      mo.observe(anchorRoot, { childList: true, subtree: true });
      var modalRoot = document.querySelector(modalSel);
      if (modalRoot) {
        mo.observe(modalRoot, { childList: true, subtree: true });
        modalRoot.addEventListener('shown.bs.modal', function () {
          bindDirectModelViewerGather(gatherProductModelViewers, onViewerGraphDirect);
          dispatchModelViewerSchedules();
          scheduleStaggeredReapply();
        });
      }
      dispatchModelViewerSchedules();
    }

    bindWhenModelViewerReady();

    /* Theme editor: section re-render keeps .is-active swatches — re-apply fabric with new tile settings. */
    if (syncFabricColorStateFromUi(wrap, state)) {
      s3dTrace('init:restored fabric/color from active UI, scheduling apply');
      enqueueApply(function () {
        return applyFabricAndColor();
      });
    }

    // ── External reset/clear signals from the flow controller ─────────────────
    // 's3d:clear-fabric' keeps initial model materials untouched until the
    // shopper explicitly selects fabric/color again.
    // 's3d:reset-fabric' is kept for backward compatibility.
    if (fabricKeys.length) {
      function clearFabricUiState() {
        wrap.querySelectorAll('.s3d-mat-config__btn[data-s3d-fabric].is-active').forEach(function (b) {
          b.classList.remove('is-active');
        });
        wrap.querySelectorAll('[data-s3d-color].is-active').forEach(function (b) {
          b.classList.remove('is-active');
        });
        wrap.querySelectorAll('[data-s3d-color-section]').forEach(function (sec) {
          sec.style.display = 'none';
        });
        wrap.querySelectorAll('[data-s3d-color-name]').forEach(function (nameEl) {
          nameEl.textContent = '';
        });
      }

      anchorRoot.addEventListener('s3d:clear-fabric', function () {
        state.fabricKey = null;
        state.colorKey = null;
        clearFabricUiState();
      }, true);

      anchorRoot.addEventListener('s3d:reset-fabric', function () {
        state.fabricKey = fabricKeys[0];
        state.colorKey  = firstColorKey(fabricKeys[0]);
        clearFabricUiState();
        var firstFabricBtn = wrap.querySelector('.s3d-mat-config__btn[data-s3d-fabric="' + fabricKeys[0] + '"]');
        if (firstFabricBtn) firstFabricBtn.classList.add('is-active');
        var firstColorSection = wrap.querySelector('[data-s3d-color-section="' + fabricKeys[0] + '"]');
        if (firstColorSection) {
          firstColorSection.style.display = '';
          var firstSwatch = firstColorSection.querySelector('[data-s3d-color]');
          if (firstSwatch) firstSwatch.classList.add('is-active');
        }
        enqueueApply(function () { return applyFabricAndColor(); });
      }, true);
    }

    anchorRoot.addEventListener('s3d:bed-frame-change', function () {
      enqueueApply(function () { return applyInnerBoxColor(); });
    }, true);

    // ── Size scaling via camera-orbit radius ────────────────────────────────
    // Uses getCameraOrbit().radius — always returns real numbers unlike fieldOfView.
    // scale > 1  → camera moves closer → model appears larger  (Super King, Emperor)
    // scale < 1  → camera moves further → model appears smaller (Double)
    // baseRadius = auto-fit radius (= scale 1.0 reference), captured once per model.
    // Lazy-capture handles the common case where model loads before JS is ready.


    // ── Model switching via native Shopify variant selection ─────────────────
    // Build a reverse map: variant option value → model key
    // Each model entry in custom.model_variants can carry a "variant_options" array
    // listing the Shopify option values that should trigger that model.
    // e.g. { "ottoman": { "url": "...", "has_legs": false,
    //          "variant_options": ["Manual Storage Ottoman", "Electric Ottoman"] } }
    // Maps each keyword → model key AND optionally a per-keyword URL override.
    // Also stores a normalised (no price suffix, lowercase) version for fallback matching.
    var variantOptionToModelKey = {};
    var variantOptionToUrl = {}; // populated only when entry.variant_urls exists
    // Normalised map: s3dNormKw(keyword) → { mkey, kw } for price-suffix-insensitive fallback.
    var variantOptionToModelKeyNorm = {};
    function s3dNormKw(str) {
      return String(str || '').trim()
        .replace(/\s*\(\s*\+\s*[^)]+\)\s*$/, '')
        .trim()
        .toLowerCase();
    }
    modelKeys.forEach(function (mkey) {
      var entry = modelsData[mkey];
      var opts = (entry && (entry.variant_options || entry.variant_keywords));
      opts = Array.isArray(opts) ? opts : [];
      var kwUrls = (entry && entry.variant_urls && typeof entry.variant_urls === 'object')
        ? entry.variant_urls : {};
      opts.forEach(function (opt) {
        variantOptionToModelKey[opt] = mkey;
        if (kwUrls[opt]) variantOptionToUrl[opt] = kwUrls[opt];
        var norm = s3dNormKw(opt);
        if (!variantOptionToModelKeyNorm[norm]) {
          variantOptionToModelKeyNorm[norm] = { mkey: mkey, kw: opt };
        }
      });
      // Also index size half of "Size|Drawers" combo keys so Size selectors resolve a model key
      Object.keys(kwUrls).forEach(function (comboKey) {
        var sizePart = String(comboKey).split('|')[0].trim();
        if (!sizePart) return;
        if (!variantOptionToModelKey[sizePart]) variantOptionToModelKey[sizePart] = mkey;
        var sn = s3dNormKw(sizePart);
        if (sn && !variantOptionToModelKeyNorm[sn]) {
          variantOptionToModelKeyNorm[sn] = { mkey: mkey, kw: sizePart };
        }
      });
    });

    function bedFrameOptionValues() {
      var values = [];
      modelKeys.forEach(function (mkey) {
        var entry = modelsData[mkey];
        var kws = (entry && (entry.variant_keywords || entry.variant_options)) || [];
        if (!Array.isArray(kws)) return;
        kws.forEach(function (kw) {
          var v = String(kw || '').trim();
          if (v) values.push(v);
        });
      });
      return values;
    }

    function s3dNormDrawer(str) {
      var n = s3dNormKw(str);
      if (!n || n === 'none' || n === 'no drawers' || n === 'no drawer' || n === '0 drawers' || n === '0 drawer') {
        return '0 drawer';
      }
      if (n === '4 drawer' || n === '4 drawers') return '4 drawers';
      if (n === '2+2 drawer' || n === '2+2 drawers' || n === '2 + 2 drawers' || n === '2 + 2 drawer') {
        return '2+2 drawers';
      }
      return n;
    }

    function s3dDrawerAliases(norm) {
      if (norm === '0 drawer') return ['0 drawer', '0 drawers', 'none', 'no drawers', 'no drawer'];
      if (norm === '4 drawers') return ['4 drawers', '4 drawer'];
      if (norm === '2+2 drawers') return ['2+2 drawers', '2+2 drawer', '2 + 2 drawers', '2 + 2 drawer'];
      return [norm];
    }

    function readCurrentDrawersValue() {
      var sec = document.getElementById('shopify-section-' + sectionId) || anchorRoot;
      if (!sec) return '';
      var drawSel = sec.querySelector('select[id^="dv-draw-"]');
      if (drawSel && drawSel.value) return String(drawSel.value).trim();
      var fieldsets = sec.querySelectorAll('fieldset');
      for (var i = 0; i < fieldsets.length; i++) {
        var leg = fieldsets[i].querySelector('legend span');
        if (!leg) continue;
        if (leg.textContent.trim().toLowerCase() !== 'drawers') continue;
        var checked = fieldsets[i].querySelector('input[type="radio"]:checked');
        if (checked && checked.value) return String(checked.value).trim();
      }
      return '';
    }

    // Resolve GLB for Size×Drawers combo keys like "King|0 Drawer"
    function resolveVariantUrl(mk, sizeVal) {
      var entry = modelsData[mk];
      if (!entry) return null;
      var kwUrls = (entry.variant_urls && typeof entry.variant_urls === 'object') ? entry.variant_urls : {};
      var sizeNorm = s3dNormKw(sizeVal);
      if (!sizeNorm) return null;
      if (kwUrls[sizeVal]) return kwUrls[sizeVal];

      var drawersRaw = readCurrentDrawersValue();
      var drawersNorm = drawersRaw ? s3dNormDrawer(drawersRaw) : '0 drawer';
      var aliases = s3dDrawerAliases(drawersNorm);

      var comboHit = null;
      var sizeZero = null;
      var sizeAny = null;
      Object.keys(kwUrls).forEach(function (comboKey) {
        var parts = String(comboKey).split('|');
        if (s3dNormKw(parts[0] || '') !== sizeNorm) return;
        if (!sizeAny) sizeAny = kwUrls[comboKey];
        if (parts.length === 1) {
          if (!comboHit) comboHit = kwUrls[comboKey];
          return;
        }
        var kDraw = s3dNormDrawer(parts[1] || '');
        if (kDraw === '0 drawer' && !sizeZero) sizeZero = kwUrls[comboKey];
        if (aliases.indexOf(kDraw) !== -1) comboHit = kwUrls[comboKey];
      });
      return comboHit || sizeZero || sizeAny || null;
    }

    // Apply a model key, optionally overriding the GLB URL for this specific keyword.
    function applyModelKey(mk, urlOverride) {
      var entry = modelsData[mk];
      if (!entry) return;
      var resolvedUrl = urlOverride || entry.url || entry.glb_url || entry.model_url;
      if (!resolvedUrl) return;
      s3dTrace('ui:native-variant → model', { modelKey: mk, url: resolvedUrl });
      state.modelKey = mk;
      // Show/hide leg section
      var modelHasLegs = entry.has_legs !== false;
      var legSection = wrap.querySelector('[data-s3d-leg-section]');
      if (legSection) legSection.style.display = modelHasLegs ? '' : 'none';
      // Swap model-viewer src only if it differs (avoids unnecessary reloads)
      var viewers = gatherProductModelViewers();
      viewers.forEach(function (mv) {
        if (mv.getAttribute('src') === resolvedUrl) return; // already correct model
        var progressSlot = mv.querySelector('[slot="progress-bar"]');
        if (progressSlot) progressSlot.style.removeProperty('display');
        mv.setAttribute('src', resolvedUrl);
      });
    }

    function detectAndApplyModelFromValue(v) {
      if (!v) return false;
      var val = String(v).trim();
      var valNorm = s3dNormKw(val);
      // Ignore pure drawer option changes here — handled via size re-resolve below
      if (valNorm === '0 drawer' || valNorm === '0 drawers' || valNorm === '4 drawers' || valNorm === '4 drawer'
        || valNorm === '2+2 drawers' || valNorm === '2+2 drawer' || valNorm.indexOf('drawer') !== -1) {
        // Re-apply using current size so Size|Drawers combo updates
        var sizeSel = (document.getElementById('shopify-section-' + sectionId) || anchorRoot);
        var sizeEl = sizeSel && sizeSel.querySelector('select[id^="dv-size-"]');
        var sizeVal = sizeEl && sizeEl.value ? sizeEl.value : '';
        if (!sizeVal) {
          var fieldsets = (document.getElementById('shopify-section-' + sectionId) || anchorRoot).querySelectorAll('fieldset');
          for (var fi = 0; fi < fieldsets.length; fi++) {
            var leg = fieldsets[fi].querySelector('legend span');
            if (!leg) continue;
            var lt = leg.textContent.trim().toLowerCase();
            if (lt !== 'size' && lt !== 'bed size') continue;
            var checked = fieldsets[fi].querySelector('input[type="radio"]:checked');
            if (checked) sizeVal = checked.value;
          }
        }
        if (sizeVal) return detectAndApplyModelFromValue(sizeVal);
        return false;
      }
      // 1. Exact match
      var mk = variantOptionToModelKey[val];
      var matchedKw = val;
      // 2. Normalised fallback — tolerates price-suffix differences and case
      if (!mk) {
        var normEntry = variantOptionToModelKeyNorm[valNorm];
        if (normEntry) { mk = normEntry.mkey; matchedKw = normEntry.kw; }
      }
      if (!mk) return false;
      // Prefer combo Size|Drawers URL; never fall back to default entry.url when combos exist for this size
      var urlOverride = variantOptionToUrl[val] || variantOptionToUrl[matchedKw] || resolveVariantUrl(mk, val);
      if (!urlOverride) {
        var entry = modelsData[mk];
        var hasCombos = entry && entry.variant_urls && Object.keys(entry.variant_urls).some(function (k) {
          return String(k).indexOf('|') !== -1 && s3dNormKw(String(k).split('|')[0]) === valNorm;
        });
        if (hasCombos) return false; // let page-level sync handle it; do not reset to Single
        urlOverride = null; // applyModelKey will use entry.url
      }
      applyModelKey(mk, urlOverride);
      return true;
    }

    if (Object.keys(variantOptionToModelKey).length) {
      var secElModel = document.getElementById('shopify-section-' + sectionId) || anchorRoot;

      // Native radio change (standard Shopify variant buttons)
      secElModel.addEventListener('change', function (ev) {
        var val = ev.target && ev.target.value ? String(ev.target.value).trim() : '';
        detectAndApplyModelFromValue(val);
        if (val && bedFrameOptionValues().indexOf(val) !== -1) {
          enqueueApply(function () { return applyInnerBoxColor(); });
        }
      });

      // Label click → read visually-hidden radio value
      secElModel.addEventListener('click', function (ev) {
        var label = ev.target.closest('label[for]');
        if (!label) return;
        var input = document.getElementById(label.htmlFor);
        if (input && input.type === 'radio') {
          setTimeout(function () {
            var val = String(input.value || '').trim();
            detectAndApplyModelFromValue(val);
            if (val && bedFrameOptionValues().indexOf(val) !== -1) {
              enqueueApply(function () { return applyInnerBoxColor(); });
            }
          }, 0);
        }
      });

      // variant:change custom event (fired by some themes)
      document.addEventListener('variant:change', function (ev) {
        var detail = ev.detail || {};
        var variant = detail.variant || detail;
        var options = (variant && variant.options) ? variant.options : [];
        for (var i = 0; i < options.length; i++) {
          if (detectAndApplyModelFromValue(options[i])) return;
        }
      });

      // Initial state: detect currently-selected native variant on page load
      (function () {
        var secEl = document.getElementById('shopify-section-' + sectionId) || anchorRoot;
        var radios = secEl.querySelectorAll('input[type="radio"]:checked');
        for (var r = 0; r < radios.length; r++) {
          if (detectAndApplyModelFromValue(radios[r].value)) return;
        }
      })();
    }
    // ── End model switching ───────────────────────────────────────────────────

    // Fabric selector buttons — switch active fabric, swap color section, apply
    wrap.querySelectorAll('.s3d-mat-config__btn[data-s3d-fabric]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var fk = btn.getAttribute('data-s3d-fabric');
        s3dTrace('ui:fabric-btn click', { fabricKey: fk });
        state.fabricKey = fk;
        state.colorKey = firstColorKey(fk);
        // Active state on fabric buttons
        wrap.querySelectorAll('.s3d-mat-config__btn[data-s3d-fabric]').forEach(function (b) {
          b.classList.toggle('is-active', b.getAttribute('data-s3d-fabric') === fk);
        });
        // Show the matching color section, hide the rest
        wrap.querySelectorAll('[data-s3d-color-section]').forEach(function (sec) {
          sec.style.display = sec.getAttribute('data-s3d-color-section') === fk ? '' : 'none';
        });
        // Reset first swatch in the new section as active and clear color name
        var newSection = wrap.querySelector('[data-s3d-color-section="' + fk + '"]');
        if (newSection) {
          newSection.querySelectorAll('[data-s3d-color]').forEach(function (b, i) {
            b.classList.toggle('is-active', i === 0);
          });
        }
        // Clear selected color name when fabric switches
        var nameEl = wrap.querySelector('[data-s3d-color-name="' + fk + '"]');
        if (nameEl) nameEl.textContent = '';
        enqueueApply(function () {
          return applyFabricAndColor();
        });
        scheduleCartPropertySync(wrap, fabricsData, state);
      });
    });

    wrap.querySelectorAll('[data-s3d-color]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        // Ignore buttons belonging to a different (hidden) fabric section
        var fref = btn.getAttribute('data-s3d-fabric-ref');
        if (fref && fref !== state.fabricKey) return;
        var ck = btn.getAttribute('data-s3d-color');
        s3dTrace('ui:color-swatch click', { colorKey: ck, fabricKey: state.fabricKey });
        state.colorKey = ck;
        // Update active only within the current fabric's color section
        var activeSection = wrap.querySelector('[data-s3d-color-section="' + state.fabricKey + '"]') || wrap;
        activeSection.querySelectorAll('[data-s3d-color]').forEach(function (b) {
          b.classList.toggle('is-active', b.getAttribute('data-s3d-color') === ck);
        });
        // Update the selected color name label
        var nameEl = wrap.querySelector('[data-s3d-color-name="' + state.fabricKey + '"]');
        if (nameEl) nameEl.textContent = ck;
        enqueueApply(function () {
          return applyFabricAndColor();
        });
        scheduleCartPropertySync(wrap, fabricsData, state);
      });
    });

    wrap.querySelectorAll('[data-s3d-leg]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var k = btn.getAttribute('data-s3d-leg');
        s3dTrace('ui:leg-button click', { legKey: k });
        state.legKey = k;
        wrap.querySelectorAll('[data-s3d-leg]').forEach(function (b) {
          b.classList.toggle('is-active', b.getAttribute('data-s3d-leg') === k);
        });
        enqueueApply(function () {
          return applyLegs();
        });
      });
    });

    wrap.addEventListener('s3d:sync-cart-properties', function () {
      syncFabricColorStateFromUi(wrap, state);
      syncCartLineItemProperties(wrap, fabricsData, state);
    });

    var productForm =
      document.getElementById('product-form-' + sectionId) ||
      document.querySelector('.js-product-wrapper form[data-type="add-to-cart-form"]');
    if (productForm && !productForm.__s3dCartPropSyncBound) {
      productForm.__s3dCartPropSyncBound = true;
      productForm.addEventListener('submit', function () {
        syncFabricColorStateFromUi(wrap, state);
        syncCartLineItemProperties(wrap, fabricsData, state);
      });
    }
  }

  function boot() {
    var roots = document.querySelectorAll('[data-s3d-material-configurator]');
    s3dTrace('boot: found configurator roots', { count: roots.length });
    roots.forEach(initConfigurator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    if (ev.target && ev.target.querySelectorAll) {
      var inSection = ev.target.querySelectorAll('[data-s3d-material-configurator]');
      if (inSection.length) {
        s3dTrace('shopify:section:load re-init', { count: inSection.length });
      }
      inSection.forEach(function (w) {
        w.removeAttribute('data-s3d-initialized');
        initConfigurator(w);
      });
    }
  });
})();
